import { useEffect, useRef, useState, type ReactNode } from "react";
import { endMetadataMerge } from "../../../lib/metadata-history/dispatch";

type PlacementNumberFieldProps = {
  id: string;
  value: number;
  label: ReactNode;
  title?: string;
  rotation?: boolean;
  size?: boolean;
  opacity?: boolean;
  precision?: number;
  disabled?: boolean;
  onChange: (value: number) => void;
};

// Parse arithmetic without executing user input as JavaScript.
function evaluate(text: string): number {
  const tokens = text.match(/\d+(?:\.\d*)?|\.\d+|[()+*/-]/g) ?? [];
  if (tokens.join("") !== text.replace(/\s/g, "")) return NaN;
  let index = 0;
  function primary(): number {
    const token = tokens[index++];
    if (token === "+") return primary();
    if (token === "-") return -primary();
    if (token === "(") {
      const value = expression();
      return tokens[index++] === ")" ? value : NaN;
    }
    return token === undefined ? NaN : Number(token);
  }
  function product(): number {
    let value = primary();
    while (tokens[index] === "*" || tokens[index] === "/") {
      const operator = tokens[index++];
      const right = primary();
      value = operator === "*" ? value * right : value / right;
    }
    return value;
  }
  function expression(): number {
    let value = product();
    while (tokens[index] === "+" || tokens[index] === "-") {
      const operator = tokens[index++];
      const right = product();
      value = operator === "+" ? value + right : value - right;
    }
    return value;
  }
  const result = expression();
  return index === tokens.length ? result : NaN;
}

export function PlacementNumberField({ id, value, label, title, rotation, size, opacity, precision = size ? 2 : 0, disabled, onChange }: PlacementNumberFieldProps) {
  const [text, setText] = useState(String(value));
  const committed = useRef(value);
  const drag = useRef<{ x: number; value: number } | null>(null);
  useEffect(() => {
    if (value === committed.current) return;
    committed.current = value;
    setText(String(value));
  }, [value]);
  const apply = (next: number) => {
    if (!Number.isFinite(next)) { setText(String(committed.current)); return committed.current; }
    const factor = 10 ** precision;
    const rounded = Math.round(next * factor) / factor;
    const sized = size ? Math.max(0.01, rounded) : rounded;
    const angle = rotation ? Math.round((((sized + 180) % 360 + 360) % 360 - 180) * factor) / factor : sized;
    const normalized = opacity ? Math.min(100, Math.max(0, angle)) : angle;
    setText(String(normalized));
    const changed = normalized !== committed.current;
    committed.current = normalized;
    if (changed) onChange(normalized);
    return normalized;
  };
  return (
    <div data-placement-field className="group flex h-8 min-w-0 flex-1 items-center gap-1.5 border-r border-neutral-200 bg-white px-2 last:border-r-0 focus-within:bg-white focus-within:shadow-[inset_0_0_0_1.5px_#185fa5] has-disabled:opacity-50">
      <label htmlFor={id} title={title} className="min-w-3 shrink-0 cursor-ew-resize touch-none select-none text-[13px] text-neutral-400 group-hover:text-neutral-600 group-focus-within:text-neutral-600"
        onPointerDown={event => {
          if (disabled) return;
          event.preventDefault();
          drag.current = { x: event.clientX, value: committed.current };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={event => {
          if (!drag.current) return;
          apply(drag.current.value + Math.round((event.clientX - drag.current.x) / 2) * (event.shiftKey ? 10 : 1));
        }}
        onPointerUp={event => { event.currentTarget.releasePointerCapture(event.pointerId); }}
        onLostPointerCapture={() => { drag.current = null; endMetadataMerge(); }}>
        {label}
      </label>
      <input id={id} disabled={disabled} value={text} className="w-full min-w-0 border-0 bg-transparent p-0 text-sm text-neutral-900 outline-none"
        onChange={event => {
          const draft = event.target.value;
          setText(draft);
          // Keep incomplete numbers and arithmetic editable until commit.
          if (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(draft)) return;
          const next = Number(draft);
          if (!Number.isFinite(next)) return;
          if (size && next < 0.01) return;
          if (opacity && (next < 0 || next > 100)) return;
          if (rotation && (next < -180 || next >= 180)) return;
          if (next === committed.current) return;
          committed.current = next;
          onChange(next);
        }} onFocus={event => event.target.select()}
        onBlur={() => { apply(evaluate(text)); endMetadataMerge(); }}
        onKeyDown={event => {
          if (event.key === "Enter") { apply(evaluate(text)); event.currentTarget.select(); }
          if (event.key === "Escape") { setText(String(committed.current)); event.currentTarget.select(); }
          if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
            const base = evaluate(text);
            const direction = event.key === "ArrowUp" ? 1 : -1;
            apply((Number.isFinite(base) ? base : committed.current) + direction * (event.shiftKey ? 10 : 1));
            event.currentTarget.select();
          }
        }} />
      {(rotation || size || opacity) && <span className="text-xs text-neutral-400">{rotation ? "°" : "%"}</span>}
    </div>
  );
}
